import { Alert, Button, IconButton, Slide, Snackbar } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useClearCache } from 'react-clear-cache';
import { confirmAlert } from 'react-confirm-alert';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Particles from 'react-tsparticles';
import 'tailwindcss/tailwind.css';
import CloseIcon from '@mui/icons-material/Close';
import { forEach, keys } from 'lodash';
import { SkillsTypes } from '@/store/ducks/skills';
import { ExperienceTypes } from '@/store/ducks/experience';
import Routes from './routes';
import GlobalStyled from './styles/GlobalStyle';
import { checkEmpty, getAnimationObject } from './utils/commonFunctions';
import { AcademicTypes } from '@/store/ducks/academic';
import { ReposTypes } from '@/store/ducks/repos';
// Single mount component
const Transition = (props) => <Slide {...props} direction="up" />;

/**
 * @summary App Component
 * @description Main component of the app
 * @param {object} props Properties of the Component
 * @param {boolean} primaryLoader Flag for enabling/disabling primary loader
 * @param {object} reposError Repos error object
 * @param {object} skillsError Skills error object
 * @param {object} experienceError Experience error object
 * @param {object} academicError Academic error object
 * @author Papu Kumar <papuruth@gmail.com>
 * @component
 * @returns {JSX.Element} Return optimised JSX to be rendered.
 */
function App({
  primaryLoader,
  reposError,
  skillsError,
  experienceError,
  academicError,
  getReposRequest,
  getSkillsRequest,
  getAcademicRequest,
  getExperienceRequest,
}) {
  const { isLatestVersion, emptyCacheStorage } = useClearCache();
  const [showAPIErrorAlert, setShowAPIErrorAlert] = useState(false);
  const [apiErrorTypes, setAPIErrorTypes] = useState({});

  const { type: repoType } = reposError || {};
  const { type: skillType } = skillsError || {};
  const { type: academicType } = academicError || {};
  const { type: expType } = experienceError || {};

  useEffect(() => {
    const updateVersion = () => {
      confirmAlert({
        title: 'Papu Kumar | Portfolio',
        message: 'A new version is available. Please click on the button to update to the latest version.',
        buttons: [
          {
            label: 'Update Version',
            onClick: () => {
              emptyCacheStorage();
            },
          },
        ],
        closeOnEscape: false,
        closeOnClickOutside: false,
      });
    };

    if (!isLatestVersion) {
      setTimeout(() => {
        if (process.env.NODE_ENV === 'production') {
          updateVersion();
        }
      }, 1000);
    }
  }, [isLatestVersion, emptyCacheStorage]);

  useEffect(() => {
    if (repoType || skillType || expType || academicType) {
      setAPIErrorTypes({ repoType, skillType, academicType, expType });
      setShowAPIErrorAlert(true);
    }
  }, [repoType, skillType, academicType, expType]);

  const handleAPICalls = (reqType) => {
    if (reqType === ReposTypes.GET_REPOS_REQUEST) {
      getReposRequest();
    }
    if (reqType === SkillsTypes.GET_SKILLS_REQUEST) {
      getSkillsRequest();
    }
    if (reqType === ExperienceTypes.GET_EXPERIENCE_REQUEST) {
      getExperienceRequest();
    }
    if (reqType === AcademicTypes.GET_ACADEMIC_REQUEST) {
      getAcademicRequest();
    }
  };

  const handleAPIRetry = () => {
    setShowAPIErrorAlert(false);
    if (!checkEmpty(apiErrorTypes)) {
      forEach(keys(apiErrorTypes), (key) => {
        if (apiErrorTypes[key]) {
          handleAPICalls(apiErrorTypes[key]);
        }
      });
    }
  };

  const handleClose = () => setShowAPIErrorAlert(false);

  const apiAction = (
    <>
      <Button color="secondary" size="small" onClick={handleAPIRetry} sx={{ color: '#fff', fontWeight: 'bold' }}>
        Try Again
      </Button>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <>
      {/* App Animation Injector */}
      <Particles id="tsparticles" canvasClassName="ts__particle" options={getAnimationObject()} />
      {/* MUI CSS */}
      <CssBaseline />
      {/* Styles Component Global Styles Injector */}
      <GlobalStyled />
      {/* App Routes Handler */}
      <Routes />
      {/* Toast Container */}
      <ToastContainer autoClose={3000} closeButton pauseOnHover position="bottom-right" />
      {/* App Global Loader */}
      {primaryLoader && (
        <div id="preloader">
          <div id="loader" />
        </div>
      )}
      <Snackbar TransitionComponent={Transition} autoHideDuration={5000} open={showAPIErrorAlert}>
        <Alert severity="error" action={apiAction} variant="filled">
          API Error!
        </Alert>
      </Snackbar>
    </>
  );
}

App.propTypes = {
  primaryLoader: PropTypes.bool.isRequired,
  reposError: PropTypes.oneOfType([PropTypes.object]).isRequired,
  skillsError: PropTypes.oneOfType([PropTypes.object]).isRequired,
  experienceError: PropTypes.oneOfType([PropTypes.object]).isRequired,
  academicError: PropTypes.oneOfType([PropTypes.object]).isRequired,
  getReposRequest: PropTypes.func.isRequired,
  getSkillsRequest: PropTypes.func.isRequired,
  getAcademicRequest: PropTypes.func.isRequired,
  getExperienceRequest: PropTypes.func.isRequired,
};

export default App;
